/**
 * OutputFieldTable (#2373) — the per-field detail table under the Ernteprognose
 * chart. Rows = the selected Kultur·Sorte's current-WJ field-cycles (one per
 * Schlag); columns Schlag / Fläche / Ist / Prognose / Plan / Ist·Prognose % /
 * letzter Pflücktag. Sortable (component-local state, NOT a URL param), a Σ
 * ``<tfoot>`` row, and a row click that drives ``?f=`` + highlights — mirroring
 * Deckungsbeitrag's RankedMarginTable (+ its #2345 CSS). All shaping is in the
 * pure ``outputFieldTable`` helper; this stays a thin declarative view.
 *
 * #2459 — the Σ ``Gesamt`` row is itself SELECTABLE (and the default selection):
 * clicking a Schlag narrows the chart to that field-cycle (``?f=``); clicking
 * Gesamt returns to the group aggregate (clears ``?f=``). ``selectedId == null``
 * ⇒ Gesamt is the selected row (no arbitrary Schlag pre-highlighted).
 */
import { useMemo, useState } from 'react';
import {
  FIELD_NUMERIC_KEYS,
  fieldRowsTotal,
  sortFieldRows,
  type FieldRow,
  type FieldSortDir,
  type FieldSortKey,
} from './outputFieldRows';
import './OutputFieldTable.css';

interface FieldColumn {
  key: FieldSortKey;
  label: string;
  numeric: boolean;
}

const COLUMNS: FieldColumn[] = [
  { key: 'schlag', label: 'Schlag', numeric: false },
  { key: 'areaHa', label: 'Fläche (ha)', numeric: true },
  { key: 'istKg', label: 'Ist (kg)', numeric: true },
  { key: 'prognoseKg', label: 'Prognose (kg)', numeric: true },
  { key: 'planKg', label: 'Plan (kg)', numeric: true },
  { key: 'erfuellung', label: 'Ist / Prognose', numeric: true },
  { key: 'letzterPfluecktag', label: 'Letzter Pflücktag', numeric: false },
];

const DEFAULT_SORT: { key: FieldSortKey; dir: FieldSortDir } = { key: 'schlag', dir: 'asc' };

const kgFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const haFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
const pctFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

const fmtKg = (v: number | null): string => (v == null ? '—' : kgFmt.format(Math.round(v)));
const fmtHa = (v: number | null): string => (v == null ? '—' : haFmt.format(v));
const fmtPct = (v: number | null): string => (v == null ? '—' : `${pctFmt.format(v * 100)} %`);
const fmtDate = (v: string | null): string => {
  if (v == null) return '—';
  const [y, m, d] = v.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};

export interface OutputFieldTableProps {
  rows: FieldRow[];
  /** #2459 — the selected Schlag's field-cycle, or ``null`` ⇒ Gesamt (aggregate) is selected. */
  selectedId?: string | null;
  /** Row click → select that Schlag (drives ``?f=`` + narrows the chart). */
  onSelect: (cropCycleId: string) => void;
  /** #2459 — Gesamt row click → return to the group aggregate (clears ``?f=``). */
  onSelectTotal: () => void;
}

export default function OutputFieldTable({ rows, selectedId, onSelect, onSelectTotal }: OutputFieldTableProps) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  // #2459 — no Schlag selected ⇒ Gesamt is the selected (default) row.
  const totalSelected = selectedId == null;

  const sorted = useMemo(() => sortFieldRows(rows, sort.key, sort.dir), [rows, sort.key, sort.dir]);
  const total = useMemo(() => fieldRowsTotal(rows), [rows]);

  const toggleSort = (key: FieldSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: FIELD_NUMERIC_KEYS.has(key) ? 'desc' : 'asc' },
    );
  };

  const cellClass = (key: FieldSortKey, numeric: boolean): string => {
    const base = numeric ? 'output-field-table__num' : '';
    return sort.key === key ? `${base} output-field-table__col--active`.trim() : base;
  };

  if (rows.length === 0) {
    return (
      <p className="output-field-table__empty" role="status">
        Für diese Kultur sind in der laufenden Saison keine Schläge erfasst.
      </p>
    );
  }

  return (
    <div className="output-field-table-scroll">
      <table className="output-field-table">
        <thead>
          <tr>
            {COLUMNS.map((col) => {
              const active = sort.key === col.key;
              const thClass = [
                col.numeric ? 'output-field-table__num' : '',
                active ? 'output-field-table__col--active' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={thClass || undefined}
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <button type="button" className="output-field-table__sort" onClick={() => toggleSort(col.key)}>
                    {col.label}
                    {active && (
                      <span className="output-field-table__sort-indicator" aria-hidden="true">
                        {sort.dir === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const selected = row.cropCycleId === selectedId;
            const rowClass = ['output-field-table__row', selected ? 'output-field-table__row--selected' : '']
              .filter(Boolean)
              .join(' ');
            return (
              <tr
                id={`cc-${row.cropCycleId}`}
                key={row.cropCycleId}
                className={rowClass}
                onClick={() => onSelect(row.cropCycleId)}
              >
                <td className={cellClass('schlag', false)}>
                  <button
                    type="button"
                    className="output-field-table__leaf-button"
                    aria-pressed={selected}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(row.cropCycleId);
                    }}
                  >
                    {row.schlag}
                  </button>
                </td>
                <td className={cellClass('areaHa', true)}>{fmtHa(row.areaHa)}</td>
                <td className={cellClass('istKg', true)}>{fmtKg(row.istKg)}</td>
                <td className={cellClass('prognoseKg', true)}>{fmtKg(row.prognoseKg)}</td>
                <td className={cellClass('planKg', true)}>{fmtKg(row.planKg)}</td>
                <td className={cellClass('erfuellung', true)}>{fmtPct(row.erfuellung)}</td>
                <td className={cellClass('letzterPfluecktag', false)}>{fmtDate(row.letzterPfluecktag)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr
            className={[
              'output-field-table__total',
              'output-field-table__row',
              totalSelected ? 'output-field-table__row--selected' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onSelectTotal}
          >
            <th scope="row">
              <button
                type="button"
                className="output-field-table__leaf-button"
                aria-pressed={totalSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectTotal();
                }}
              >
                Gesamt
              </button>
            </th>
            <td className="output-field-table__num">{fmtHa(total.areaHa)}</td>
            <td className="output-field-table__num">{fmtKg(total.istKg)}</td>
            <td className="output-field-table__num">{fmtKg(total.prognoseKg)}</td>
            <td className="output-field-table__num">{fmtKg(total.planKg)}</td>
            <td className="output-field-table__num">{fmtPct(total.erfuellung)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
